import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  callApiWithOptions,
  callStreamingApi,
  HttpMethod,
} from "src/services/apiService";
import { useLayoutContext } from "src/contexts/LayoutContext";
import AIMarkdown from "src/components/AIMarkdown";
import TooltipButton from "src/components/TooltipButton";
import VoiceMode from "src/components/VoiceMode";
import TypingIndicator from "src/components/TypingIndicator";
import typingIndicatorStyles from "src/components/TypingIndicator.module.css";
import PaywallModal from "./PaywallModal";
import { Turnstile } from "@marsidev/react-turnstile";
import styles from "./Index.module.css";

interface ChatMessage {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
  aiInteractionId?: string;
  isStreamingComplete?: boolean;
  ragContent?: string;
  feedback?: {
    isLiked?: boolean;
    isReported?: boolean;
    reportedMessage?: string;
  };
}

interface Thread {
  threadId: string;
  threadName: string;
  messageCount: number;
  createdOn: string;
  lastModifiedOn: string;
}

interface ThreadInteraction {
  id: string;
  userMessage: string;
  aiResponse: string;
  createdOn: string;
  isLiked?: boolean;
  isReported?: boolean;
  reportMessage?: string;
  interactionId: string;
}

// interface ThreadResponse {
//   success: boolean;
//   thread: Thread;
//   interactions: ThreadInteraction[];
// }

interface AIAssistantProps {
  isPublic?: boolean;
}

const AIAssistant: React.FC<AIAssistantProps> = ({ isPublic = false }) => {
  // URL search params for debug mode
  const [searchParams] = useSearchParams();
  const debugMode = searchParams.get("debugMode") === "1";

  // Error message constants
  const KNOWLEDGE_BASE_ERROR =
    "I apologize, but I'm having trouble connecting to my knowledge base right now. Please try again in a moment, or contact support if the issue persists.";
  const LIMIT_REACHED_ERROR =
    "You've reached the limit for free AI Assistant usage.";

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [selectedPersona, setSelectedPersona] = useState<"neutral" | "donna">(
    "neutral",
  );
  const [selectedVoice, setSelectedVoice] = useState<string>("sage");

  // Prevent lint warnings for unused setters when personality UI is hidden
  void setSelectedPersona;
  const [showReportInput, setShowReportInput] = useState<string | null>(null);
  const [reportMessage, setReportMessage] = useState("");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showVoiceMode, setShowVoiceMode] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [threadToDelete, setThreadToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [threadId, setThreadId] = useState<string>("");
  const [threads, setThreads] = useState<Thread[]>([]);
  const [isLoadingThreads, setIsLoadingThreads] = useState(false);
  const [isFirstQuestion, setIsFirstQuestion] = useState(true);
  const [voiceUserMessage, setVoiceUserMessage] = useState<string>("");
  const [voiceAIMessage, setVoiceAIMessage] = useState<string>("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [containerHeight, setContainerHeight] = useState(0);
  const [lastMessagesHeight, setLastMessagesHeight] = useState(0);
  const voiceUserMessageIdRef = useRef<string | null>(null);
  const voiceAIMessageIdRef = useRef<string | null>(null);
  const { setHeading, setTagline } = useLayoutContext();

  // Public-specific state
  const [anonymousId, setAnonymousId] = useState<string | null>(null);
  const [email, setEmail] = useState<string>("");
  const [hasEmail, setHasEmail] = useState<boolean>(false);
  const [isEmailVerified, setIsEmailVerified] = useState<boolean>(false);
  const [showPaywall, setShowPaywall] = useState<boolean>(false);
  const [cloudflareTurnstileRequired, setCloudflareTurnstileRequired] =
    useState<boolean>(false);
  const [showCloudflareTurnstile, setShowCloudflareTurnstile] =
    useState<boolean>(false);
  const [isInitializing, setIsInitializing] = useState<boolean>(false);
  const [isLimitReached, setIsLimitReached] = useState<boolean>(false);

  useEffect(() => {
    // Set container height based on isPublic prop
    const containerHeight = isPublic ? "100vh" : "calc(100vh - 180px)";
    document.documentElement.style.setProperty(
      "--container-height",
      containerHeight,
    );

    if (setHeading && setTagline) {
      setHeading("AI Compliance Assistant");
      setTagline(
        "Ask questions about compliance regulations and get instant answers from our knowledge base",
      );
    }
  }, [setHeading, setTagline]);

  // Track container height for dynamic padding calculation
  useEffect(() => {
    const updateContainerHeight = () => {
      if (messagesContainerRef.current) {
        const rect = messagesContainerRef.current.getBoundingClientRect();
        setContainerHeight(rect.height);
      }
    };

    // Initial measurement
    updateContainerHeight();

    // Use ResizeObserver for accurate container size tracking
    let resizeObserver: ResizeObserver | null = null;
    if (messagesContainerRef.current) {
      resizeObserver = new ResizeObserver(updateContainerHeight);
      resizeObserver.observe(messagesContainerRef.current);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  // Calculate height of last aiMessage and userMessage divs
  // so that we can add padding-bottom to the messages container
  // so the users message is always at the top of the container
  useEffect(() => {
    const calculateLastMessagesHeight = () => {
      if (!messagesContainerRef.current) return;

      const messageElements = messagesContainerRef.current.querySelectorAll(
        `.${styles.aiMessage}, .${styles.userMessage}`,
      );

      if (messageElements.length === 0) {
        setLastMessagesHeight(0);
        return;
      }

      // Get the last two messages (if they exist)
      const lastElements = Array.from(messageElements).slice(-2);
      let totalHeight = 0;

      lastElements.forEach((element) => {
        const rect = element.getBoundingClientRect();
        totalHeight += rect.height;
      });

      setLastMessagesHeight(totalHeight);
    };

    // Calculate on messages change
    calculateLastMessagesHeight();

    // Use MutationObserver to watch for DOM changes in messages
    let mutationObserver: MutationObserver | null = null;
    if (messagesContainerRef.current) {
      mutationObserver = new MutationObserver(calculateLastMessagesHeight);
      mutationObserver.observe(messagesContainerRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class"],
      });
    }

    return () => {
      if (mutationObserver) {
        mutationObserver.disconnect();
      }
    };
  }, [messages]);

  useEffect(() => {
    // Add a welcome message
    const welcomeMessage: ChatMessage = {
      id: "welcome",
      text: getWelcomeMessage(selectedPersona),
      isUser: false,
      timestamp: new Date(),
      isStreamingComplete: true,
    };
    setMessages([welcomeMessage]);
  }, [selectedPersona]);

  // Initialize cookie for public users or thread for authenticated users
  useEffect(() => {
    const initializeComponent = async () => {
      if (isPublic) {
        // Check if we already have an anonymous ID to prevent duplicate calls
        if (anonymousId || isInitializing) {
          return; // Already initialized or initializing, don't call API again
        }

        // Initialize cookie for public users
        const initializeCookie = async () => {
          try {
            setIsInitializing(true);
            const response = await callApiWithOptions({
              endpoint: "ComplianceGuide/InitCookie",
              method: HttpMethod.POST,
              customHeaders: isPublic ? getCustomHeaders() : undefined,
            });

            if (response.success) {
              setAnonymousId(response.anonymousId);
              setThreadId(response.anonymousId);
              if (response.anonymousId && !localStorage.getItem("anon_Id")) {
                localStorage.setItem("anon_Id", response.anonymousId);
              }
              setEmail(response.email || "");
              setHasEmail(response.hasEmail || false);
              setIsEmailVerified(response.isEmailVerified || false);

              // Handle paywall logic for public users
              if (isPublic) {
                // Check if email is verified but no remaining messages
                if (
                  response.isEmailVerified &&
                  response.remainingMessages === 0
                ) {
                  setShowPaywall(true);
                }

                // If no remaining messages and not email verified, show paywall
                if (
                  response.remainingMessages === 0 &&
                  !response.isEmailVerified
                ) {
                  setShowPaywall(true);
                }
              }
            } else if (response.requiresCloudflareTurnstile) {
              // Handle the new response format for CloudflareTurnstile requirement
              console.log(
                "CloudflareTurnstile required due to high request rate",
              );
              console.log("AnonymousId from response:", response.anonymousId);
              // Store the anonymousId for later use
              if (response.anonymousId) {
                localStorage.setItem("anon_Id", response.anonymousId);
                setAnonymousId(response.anonymousId);
              }
              setCloudflareTurnstileRequired(true);
              setShowCloudflareTurnstile(true);
            }
          } catch (error: any) {
            // Check if this is the CloudflareTurnstile error
            if (
              error.message?.includes("CloudflareTurnstile needs to be invoked")
            ) {
              console.log(
                "CloudflareTurnstile required due to high request rate",
              );
              console.log("Full error message:", error.message);
              setCloudflareTurnstileRequired(true);
              setShowCloudflareTurnstile(true);
            } else {
              console.error("Error initializing cookie:", error);
            }
          } finally {
            setIsInitializing(false);
          }
        };

        // Only initialize if we don't have a cookie and haven't already initialized
        if (!checkAnonIdCookie() && !anonymousId && !isInitializing) {
          initializeCookie();
        }
      } else {
        // Initialize thread for authenticated users
        const initializeThread = async () => {
          try {
            const response = await callApiWithOptions({
              endpoint: "ComplianceGuide/InitThread",
              method: HttpMethod.POST,
            });

            if (response.success && response.threadId) {
              setThreadId(response.threadId);
            } else {
              console.warn("Failed to initialize thread:", response.message);
            }
          } catch (error) {
            console.error("Error initializing thread:", error);
          }
        };

        // Only initialize thread if we don't already have one
        if (!threadId || threadId.trim() === "") {
          initializeThread();
        }
      }
    };

    initializeComponent();
  }, [isPublic]); // Removed threadId from dependencies to prevent re-initialization

  // Fetch threads when sidebar expands
  useEffect(() => {
    if (!sidebarCollapsed) {
      fetchThreads();
    }
  }, [sidebarCollapsed]);

  // Cookie helper functions
  const getCookieValue = (name: string): string | null => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(";").shift() || null;
    }
    return null;
  };

  const checkAnonIdCookie = (): boolean => {
    const anonIdValue = getCookieValue("anon_Id");
    return anonIdValue !== null;
  };

  const getCustomHeaders = (): Record<string, string> => {
    const anonId = localStorage.getItem("anon_Id");
    if (anonId) {
      return {
        "X-Anon-ID": anonId,
      };
    }
    return {};
  };

  // CloudflareTurnstile handler functions

  const handleCloudflareTurnstileSuccess = async (token?: string) => {
    try {
      setShowCloudflareTurnstile(false);
      setCloudflareTurnstileRequired(false);

      console.log("Turnstile success - Current anonymousId:", anonymousId);
      if (token) {
        console.log("Turnstile token received:", token);
      }

      // Get anonId from state or localStorage
      let anonIdToUse = anonymousId || localStorage.getItem("anon_Id");
      console.log("anonId from state:", anonymousId);
      console.log("anonId from localStorage:", localStorage.getItem("anon_Id"));
      console.log("Final anonIdToUse for MarkCloudflarePassed:", anonIdToUse);

      // First, mark the specific anonId as having passed Cloudflare verification
      try {
        if (anonIdToUse) {
          console.log("Calling MarkCloudflarePassed with anonId:", anonIdToUse);
          const markResponse = await callApiWithOptions({
            endpoint: "ComplianceGuide/MarkCloudflarePassed",
            method: HttpMethod.POST,
            body: { AnonId: anonIdToUse },
          });

          if (markResponse.success) {
            console.log(
              "AnonId marked as Cloudflare verified:",
              markResponse.message,
              "Record age:",
              markResponse.recordAge,
              "minutes",
            );
          } else {
            console.warn(
              "Failed to mark anonId as verified:",
              markResponse.message,
            );
            // If the record is too old or not found, we should still try to continue
            // The user might need to refresh the page
          }
        } else {
          console.warn(
            "No anonymousId available to mark as Cloudflare verified - neither in state nor localStorage",
          );
        }
      } catch (markError: any) {
        console.error("Error marking anonId as verified:", markError);

        // Handle specific error cases
        if (markError.message?.includes("Record is too old")) {
          console.warn("Record is too old, user should refresh the page");
          // Could show a user-friendly message here if needed
        } else if (markError.message?.includes("not found")) {
          console.warn(
            "Record not found, possible tampering or expired session",
          );
          // Could show a user-friendly message here if needed
        }

        // Continue anyway, the main flow should still work
      }

      // Retry the original request that was blocked
      if (isPublic && !isInitializing) {
        // Re-initialize cookie for public users
        const response = await callApiWithOptions({
          endpoint: "ComplianceGuide/InitCookie",
          method: HttpMethod.POST,
          customHeaders: isPublic ? getCustomHeaders() : undefined,
        });

        if (response.success) {
          setAnonymousId(response.anonymousId);
          setThreadId(response.anonymousId);
          if (response.anonymousId && !localStorage.getItem("anon_Id")) {
            localStorage.setItem("anon_Id", response.anonymousId);
          }
          setEmail(response.email || "");
          setHasEmail(response.hasEmail || false);
          setIsEmailVerified(response.isEmailVerified || false);
        }
      }
    } catch (error: any) {
      console.error("Error retrying after CloudflareTurnstile:", error);
      // If it fails again, show the error
      if (error.message?.includes("CloudflareTurnstile needs to be invoked")) {
        setCloudflareTurnstileRequired(true);
        setShowCloudflareTurnstile(true);
      }
    }
  };

  const getWelcomeMessage = (persona: "neutral" | "donna"): string => {
    // Use the current year so the example question never feels stale.
    const currentYear = new Date().getFullYear();
    if (persona === "donna") {
      return `Hello! I'm Donna, your AI Compliance Assistant. I know TCPA, DNC regulations, and robocall rules like the back of my perfectly manicured hand. Ask me anything about telecommunications compliance - I'll give you the facts with style and just the right amount of sass. Try asking me something like 'What are the no call holidays in Louisiana in ${currentYear}?' or 'What are the calling time restrictions for Texas?'`;
    }
    return `Hello! I'm your AI Compliance Assistant. I can help you with questions about telecommunications compliance, Do Not Call regulations, and state-specific calling rules. Try asking me something like 'What are the no call holidays in Louisiana in ${currentYear}?' or 'What are the calling time restrictions for Texas?'`;
  };

  const fetchThreads = async () => {
    if (isPublic) {
      return;
    }
    try {
      setIsLoadingThreads(true);
      const response = await callApiWithOptions({
        endpoint: "ComplianceGuide/Threads",
        method: HttpMethod.GET,
      });

      if (response.success && response.threads) {
        // Filter out threads with null threadName and sort by lastModifiedOn (newest first)
        const validThreads = response.threads
          .filter((thread: Thread) => thread.threadName !== null)
          .sort(
            (a: Thread, b: Thread) =>
              new Date(b.lastModifiedOn).getTime() -
              new Date(a.lastModifiedOn).getTime(),
          );
        setThreads(validThreads);
      } else {
        console.warn("Failed to fetch threads:", response.message);
        setThreads([]);
      }
    } catch (error: any) {
      console.error("Error fetching threads:", error);
      setThreads([]);
    } finally {
      setIsLoadingThreads(false);
    }
  };

  const loadThread = async (threadId: string) => {
    try {
      const response = await callApiWithOptions({
        endpoint: `ComplianceGuide/Thread/${threadId}`,
        method: HttpMethod.GET,
      });

      if (response.success && response.thread && response.interactions) {
        // Set the current thread ID
        setThreadId(threadId);

        // Convert interactions to chat messages
        const chatMessages: ChatMessage[] = [];

        response.interactions.forEach((interaction: ThreadInteraction) => {
          // Add user message
          chatMessages.push({
            id: `user-${interaction.id}`,
            text: interaction.userMessage,
            isUser: true,
            timestamp: new Date(interaction.createdOn),
            isStreamingComplete: true,
          });

          // Add AI response
          chatMessages.push({
            id: `ai-${interaction.id}`,
            text: interaction.aiResponse,
            isUser: false,
            timestamp: new Date(interaction.createdOn),
            aiInteractionId: interaction.interactionId,
            isStreamingComplete: true,
            feedback: {
              isLiked: interaction.isLiked,
              isReported: interaction.isReported,
              reportedMessage: interaction.reportMessage,
            },
          });
        });

        // Set the messages
        setMessages(chatMessages);

        // Reset first question flag since we're loading an existing conversation
        setIsFirstQuestion(false);

        // Scroll to bottom after messages are loaded
        setTimeout(() => {
          if (messagesContainerRef.current) {
            // Scroll to bottom of thread smoothly
            messagesContainerRef.current.scrollTo({
              top: messagesContainerRef.current.scrollHeight,
              behavior: "smooth",
            });
          }
        }, 100);
      } else {
        console.warn("Failed to load thread:", response.message);
      }
    } catch (error: any) {
      console.error("Error loading thread:", error);
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    // Don't add user message to chat if limit is reached (will be handled by paywall)
    if (!isLimitReached) {
      const userMessage: ChatMessage = {
        id: Date.now().toString(),
        text: inputMessage,
        isUser: true,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMessage]);
    }

    setInputMessage("");
    setIsLoading(true);
    setIsTyping(true);

    // Scroll to bottom of scroll container smoothly
    // this combined with adding padding-bottom to the messages container
    // gets the user's message always at the top of the message container
    setTimeout(() => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTo({
          top: messagesContainerRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    }, 200); // Small delay so user sees scrolling

    const aiMessageId = (Date.now() + 1).toString();

    try {
      // Initialize thread if not already done
      let currentThreadId = threadId;
      if (!currentThreadId) {
        try {
          const response = await callApiWithOptions({
            endpoint: isPublic
              ? "ComplianceGuide/InitCookie"
              : "ComplianceGuide/InitThread",
            method: HttpMethod.POST,
            customHeaders: isPublic ? getCustomHeaders() : undefined,
          });

          if (response.success && response.threadId) {
            currentThreadId = response.threadId;
            setThreadId(currentThreadId);
          } else {
            throw new Error(response.message || "Failed to initialize thread");
          }
        } catch (error: any) {
          // Check if this is the CloudflareTurnstile error
          if (
            error.message?.includes("CloudflareTurnstile needs to be invoked")
          ) {
            console.log(
              "CloudflareTurnstile required due to high request rate",
            );
            setCloudflareTurnstileRequired(true);
            setShowCloudflareTurnstile(true);
            return; // Exit early, don't proceed with AI call
          }
          console.error("Error initializing thread:", error);
          throw error; // Re-throw to be handled by outer catch block
        }
      }
      // Use streaming API for AI responses
      let rawMarkdownText = ""; // Store unmodified markdown text
      let ragContent = ""; // Store RAG debug content

      await callStreamingApi(
        isPublic ? "ComplianceGuide/AskAIPublic" : "ComplianceGuide/AskAI",
        HttpMethod.POST,
        {
          question: inputMessage,
          persona: selectedPersona,
          threadId: currentThreadId,
          debugMode: debugMode,
        },
        // onChunk callback - called for each streamed chunk
        (chunk) => {
          if (chunk.text) {
            // Store raw unmodified markdown text
            rawMarkdownText += chunk.text;

            // Extract RAG content if present (in debug mode)
            const ragMatch = rawMarkdownText.match(
              /<<RAG_CONTENT:([\s\S]*?)>>/,
            );
            if (ragMatch) {
              ragContent = ragMatch[1];
            }

            // Remove both INTERACTION_ID and RAG_CONTENT tags from display text
            const displayText = rawMarkdownText
              .replace(/\n?<<INTERACTION_ID:[^>]+>>/, "")
              .replace(/<<RAG_CONTENT:[\s\S]*?>>/, "");

            setMessages((prev) => {
              const existingMessage = prev.find(
                (msg) => msg.id === aiMessageId,
              );
              if (!existingMessage) {
                const aiMessage: ChatMessage = {
                  id: aiMessageId,
                  text: displayText,
                  isUser: false,
                  timestamp: new Date(),
                  aiInteractionId: undefined,
                  isStreamingComplete: false,
                  ragContent: ragContent || undefined,
                  feedback: {
                    isLiked: undefined,
                    isReported: false,
                    reportedMessage: "",
                  },
                };
                return [...prev, aiMessage];
              } else {
                // Update existing message
                return prev.map((msg) =>
                  msg.id === aiMessageId
                    ? {
                        ...msg,
                        text: displayText,
                        ragContent: ragContent || undefined,
                      }
                    : msg,
                );
              }
            });
          }
        },
        // onComplete callback - called when streaming is finished
        () => {
          // Use raw unmodified markdown text after interaction ID extraction
          let finalText =
            rawMarkdownText ||
            "I'm sorry, I couldn't process your question. Please try again.";

          // Extract interaction ID from the response if present
          let extractedInteractionId: string | undefined = undefined;
          const interactionIdMatch = finalText.match(
            /<<INTERACTION_ID:([^>]+)>>/,
          );
          if (interactionIdMatch) {
            extractedInteractionId = interactionIdMatch[1];
            // Remove the interaction ID token from the display text
            finalText = finalText.replace(/\n?<<INTERACTION_ID:[^>]+>>/, "");
          }

          // Remove RAG content tag from display text
          finalText = finalText.replace(/<<RAG_CONTENT:[\s\S]*?>>/, "");

          // Final update with complete message
          setMessages((prev) => {
            const existingMessage = prev.find((msg) => msg.id === aiMessageId);
            if (!existingMessage) {
              // Create message if it doesn't exist (edge case)
              const aiMessage: ChatMessage = {
                id: aiMessageId,
                text: finalText,
                isUser: false,
                timestamp: new Date(),
                aiInteractionId: extractedInteractionId,
                isStreamingComplete: true,
                ragContent: ragContent || undefined,
                feedback: {
                  isLiked: undefined,
                  isReported: false,
                  reportedMessage: "",
                },
              };
              return [...prev, aiMessage];
            } else {
              // Update existing message
              return prev.map((msg) =>
                msg.id === aiMessageId
                  ? {
                      ...msg,
                      text: finalText,
                      aiInteractionId: extractedInteractionId,
                      isStreamingComplete: true,
                      ragContent: ragContent || undefined,
                    }
                  : msg,
              );
            }
          });

          // Focus back to the input after streaming completes
          setTimeout(() => {
            if (inputRef.current) {
              inputRef.current.focus();
            }
          }, 300); // Slightly longer delay to ensure scrolling completes first

          // Refresh threads list after first question is answered
          if (isFirstQuestion) {
            setIsFirstQuestion(false);
            fetchThreads();
          }
        },
        // onError callback - called if there's an error
        (error) => {
          console.error("Streaming API error:", error);

          // Handle specific error cases for public users
          if (
            isPublic &&
            error.status === 403 &&
            error.data &&
            error.data.error === "limit_reached"
          ) {
            // Show paywall for limit reached
            setShowPaywall(true);
            setIsLimitReached(true);

            let errorText = LIMIT_REACHED_ERROR;

            // Add signup call-to-action if redirectToSignup is true
            if (error.data.redirectToSignup) {
              errorText +=
                "\n\nPlease contact us to get unlimited access to use this feature.";
            }

            const errorMessage: ChatMessage = {
              id: aiMessageId,
              text: errorText,
              isUser: false,
              timestamp: new Date(),
              aiInteractionId: undefined,
              isStreamingComplete: true,
              feedback: {
                isLiked: undefined,
                isReported: false,
                reportedMessage: "",
              },
            };

            setMessages((prev) => {
              const existingMessage = prev.find(
                (msg) => msg.id === aiMessageId,
              );
              if (!existingMessage) {
                return [...prev, errorMessage];
              } else {
                return prev.map((msg) =>
                  msg.id === aiMessageId ? errorMessage : msg,
                );
              }
            });
            return;
          }

          // For other errors, show the standard error message
          const errorMessage: ChatMessage = {
            id: aiMessageId,
            text: KNOWLEDGE_BASE_ERROR,
            isUser: false,
            timestamp: new Date(),
            aiInteractionId: undefined,
            isStreamingComplete: true,
            feedback: {
              isLiked: undefined,
              isReported: false,
              reportedMessage: "",
            },
          };

          setMessages((prev) => {
            const existingMessage = prev.find((msg) => msg.id === aiMessageId);
            if (!existingMessage) {
              return [...prev, errorMessage];
            } else {
              return prev.map((msg) =>
                msg.id === aiMessageId ? errorMessage : msg,
              );
            }
          });
        },
        // Custom headers for public users
        isPublic ? getCustomHeaders() : undefined,
      );
    } catch (error: any) {
      // Check if this is the CloudflareTurnstile error
      if (error.message?.includes("CloudflareTurnstile needs to be invoked")) {
        console.log("CloudflareTurnstile required due to high request rate");
        console.log("Full error message:", error.message);
        setCloudflareTurnstileRequired(true);
        setShowCloudflareTurnstile(true);

        const errorMessage: ChatMessage = {
          id: aiMessageId,
          text: "Due to high request activity, a security check is required. Please complete the challenge below.",
          isUser: false,
          timestamp: new Date(),
          aiInteractionId: undefined,
          isStreamingComplete: true,
          feedback: {
            isLiked: undefined,
            isReported: false,
            reportedMessage: "",
          },
        };

        setMessages((prev) => {
          const existingMessage = prev.find((msg) => msg.id === aiMessageId);
          if (!existingMessage) {
            return [...prev, errorMessage];
          } else {
            return prev.map((msg) =>
              msg.id === aiMessageId ? errorMessage : msg,
            );
          }
        });
        return;
      }

      console.error("Error calling AI API:", error);
      const errorMessage: ChatMessage = {
        id: aiMessageId,
        text: "I apologize, but I'm having trouble connecting to my knowledge base right now. Please try again in a moment, or contact support if the issue persists.",
        isUser: false,
        timestamp: new Date(),
        aiInteractionId: undefined,
        isStreamingComplete: true,
        feedback: {
          isLiked: undefined,
          isReported: false,
          reportedMessage: "",
        },
      };

      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const startVoiceMode = () => {
    if (!showVoiceMode) {
      // Only clear chat if there are no existing messages (fresh start)
      // If there are existing messages, continue the conversation
      if (messages.length === 0) {
        setMessages([]);
      }
      setShowVoiceMode(true);
    } else {
      handleCloseVoiceMode();
    }
  };

  const handleCloseVoiceMode = () => {
    setShowVoiceMode(false);
    // Reset voice streaming state
    setVoiceUserMessage("");
    setVoiceAIMessage("");
    voiceUserMessageIdRef.current = null;
    voiceAIMessageIdRef.current = null;
  };

  // Voice streaming callback functions
  const handleUserTranscriptionDelta = (delta: string) => {
    setVoiceUserMessage((prev) => prev + delta);

    // Create or update user message in chat
    if (!voiceUserMessageIdRef.current) {
      const messageId = `voice-user-${Date.now()}`;
      voiceUserMessageIdRef.current = messageId;

      const userMessage: ChatMessage = {
        id: messageId,
        text: delta,
        isUser: true,
        timestamp: new Date(),
        isStreamingComplete: false,
      };

      setMessages((prev) => [...prev, userMessage]);
    } else {
      // Update existing message
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === voiceUserMessageIdRef.current
            ? { ...msg, text: voiceUserMessage + delta }
            : msg,
        ),
      );
    }
  };

  const handleUserTranscriptionComplete = (completeText: string) => {
    setVoiceUserMessage(completeText);

    // Mark user message as complete
    if (voiceUserMessageIdRef.current) {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === voiceUserMessageIdRef.current
            ? { ...msg, text: completeText, isStreamingComplete: true }
            : msg,
        ),
      );
      if (messagesContainerRef.current) {
        // Scroll the users question to the to top of the viewable chat container
        // just like how ChatGPT does it
        const userMessageElement = messagesContainerRef.current.querySelector(
          `[data-message-id="${voiceUserMessageIdRef.current}"]`,
        );
        if (userMessageElement) {
          const userElement = userMessageElement as HTMLElement;
          userElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "nearest",
          });
        }
      }
    }
  };

  const handleAIResponseDelta = (delta: string) => {
    setVoiceAIMessage((prev) => prev + delta);

    // Create or update AI message in chat
    if (!voiceAIMessageIdRef.current) {
      const messageId = `voice-ai-${Date.now()}`;
      voiceAIMessageIdRef.current = messageId;

      const aiMessage: ChatMessage = {
        id: messageId,
        text: delta,
        isUser: false,
        timestamp: new Date(),
        isStreamingComplete: false,
        feedback: {
          isLiked: undefined,
          isReported: false,
          reportedMessage: "",
        },
      };

      setMessages((prev) => [...prev, aiMessage]);
    } else {
      // Update existing message
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === voiceAIMessageIdRef.current
            ? { ...msg, text: voiceAIMessage + delta }
            : msg,
        ),
      );
    }
  };

  const handleAIResponseComplete = (completeText: string) => {
    setVoiceAIMessage(completeText);

    // Mark AI message as complete
    if (voiceAIMessageIdRef.current) {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === voiceAIMessageIdRef.current
            ? { ...msg, text: completeText, isStreamingComplete: true }
            : msg,
        ),
      );
    }

    // Reset for next conversation turn
    voiceUserMessageIdRef.current = null;
    voiceAIMessageIdRef.current = null;
    setVoiceUserMessage("");
    setVoiceAIMessage("");
  };

  const handleClearChat = () => {
    const welcomeMessage: ChatMessage = {
      id: "welcome",
      text: getWelcomeMessage(selectedPersona),
      isUser: false,
      timestamp: new Date(),
      isStreamingComplete: true,
    };
    setMessages([welcomeMessage]);
  };

  // handlePersonaChange removed since personality selection UI is hidden

  const handleVoiceChange = (voice: string) => {
    setSelectedVoice(voice);
    // Clear chat when voice changes since OpenAI requires conversation restart
    const welcomeMessage: ChatMessage = {
      id: "welcome",
      text: getWelcomeMessage(selectedPersona),
      isUser: false,
      timestamp: new Date(),
      isStreamingComplete: true,
    };
    setMessages([welcomeMessage]);
    // Reset thread ID to start a new conversation
    setThreadId("");
    setIsFirstQuestion(true);

    // If voice mode is currently active, restart the voice conversation
    if (showVoiceMode) {
      // Close and reopen voice mode to reinitialize with new voice
      setShowVoiceMode(false);
      // Use a small timeout to ensure cleanup is complete before reinitializing
      setTimeout(() => {
        setShowVoiceMode(true);
      }, 100);
    }
  };

  const handleNewChat = async () => {
    try {
      const response = await callApiWithOptions({
        endpoint: "ComplianceGuide/InitThread",
        method: HttpMethod.POST,
      });

      if (response.success && response.threadId) {
        setThreadId(response.threadId);
        // Also clear the chat messages
        const welcomeMessage: ChatMessage = {
          id: "welcome",
          text: getWelcomeMessage(selectedPersona),
          isUser: false,
          timestamp: new Date(),
          isStreamingComplete: true,
        };
        setMessages([welcomeMessage]);
        // Reset first question flag for new chat
        setIsFirstQuestion(true);
        // Refresh threads list
        await fetchThreads();
      } else {
        console.warn("New thread initialization failed:", response.message);
      }
    } catch (error: any) {
      console.error("Error initializing new thread:", error);
    }
  };

  const handleDeleteThread = async (
    threadIdToDelete: string,
    threadName: string,
  ) => {
    // Show confirmation modal
    setThreadToDelete({ id: threadIdToDelete, name: threadName });
    setShowDeleteConfirmModal(true);
  };

  const confirmDeleteThread = async () => {
    if (!threadToDelete) return;

    try {
      const response = await callApiWithOptions({
        endpoint: `ComplianceGuide/DeleteThread/${threadToDelete.id}`,
        method: HttpMethod.DELETE,
      });

      if (response.success) {
        console.log("Thread deleted successfully:", response.message);

        // Remove the deleted thread from the local state
        setThreads((prevThreads) =>
          prevThreads.filter((thread) => thread.threadId !== threadToDelete.id),
        );

        // If the deleted thread was the currently loaded thread, clear the chat
        if (threadToDelete.id === threadId) {
          setThreadId("");
          const welcomeMessage: ChatMessage = {
            id: "welcome",
            text: getWelcomeMessage(selectedPersona),
            isUser: false,
            timestamp: new Date(),
            isStreamingComplete: true,
          };
          setMessages([welcomeMessage]);
          setIsFirstQuestion(true);
        }

        // Optionally refresh the threads list to get updated data
        // await fetchThreads();
      } else {
        console.warn("Failed to delete thread:", response.message);
        alert("Failed to delete thread. Please try again.");
      }
    } catch (error: any) {
      console.error("Error deleting thread:", error);
      alert("Error deleting thread. Please try again.");
    } finally {
      // Close the modal
      setShowDeleteConfirmModal(false);
      setThreadToDelete(null);
    }
  };

  const cancelDeleteThread = () => {
    setShowDeleteConfirmModal(false);
    setThreadToDelete(null);
  };

  const handleFeedback = async (
    messageId: string,
    isLiked: boolean | undefined,
    isReported: boolean = false,
    reportedMessage: string = "",
  ) => {
    const message = messages.find((m) => m.id === messageId);

    if (!message) {
      return;
    }

    // If no aiInteractionId, just update local state for now
    if (!message.aiInteractionId) {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId
            ? {
                ...m,
                feedback: {
                  isLiked: isLiked,
                  isReported: isReported,
                  reportedMessage: reportedMessage,
                },
              }
            : m,
        ),
      );
      return;
    }

    try {
      const endpoint = isPublic
        ? "ComplianceGuide/SubmitAIFeedbackPublic"
        : "ComplianceGuide/SubmitAIFeedback";

      await callApiWithOptions({
        endpoint,
        method: HttpMethod.POST,
        body: {
          aiInteractionId: message.aiInteractionId,
          isLiked: isLiked,
          isReported: isReported,
          reportedMessage: reportedMessage,
        },
        customHeaders: isPublic ? getCustomHeaders() : undefined,
      });

      // Update the message with feedback
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId
            ? {
                ...m,
                feedback: {
                  isLiked: isLiked,
                  isReported: isReported,
                  reportedMessage: reportedMessage,
                },
              }
            : m,
        ),
      );

      // Hide report input if it was shown
      if (showReportInput === messageId) {
        setShowReportInput(null);
        setReportMessage("");
      }
    } catch (error: any) {
      console.error("Error submitting feedback:", error);
      console.error("Error details:", {
        message: error.message,
        status: error.status,
        data: error.data,
      });
      // You could show a toast notification here
    }
  };

  const handleReport = (messageId: string) => {
    if (showReportInput === messageId) {
      // Submit report with current message, preserving existing like/dislike state
      const message = messages.find((m) => m.id === messageId);
      const currentIsLiked = message?.feedback?.isLiked;
      handleFeedback(messageId, currentIsLiked, true, reportMessage);
    } else {
      // Show report input
      setShowReportInput(messageId);
      setReportMessage("");
    }
  };

  // Paywall handlers for public users
  const handlePaywallClose = () => {
    setShowPaywall(false);
  };

  const handleEmailSubmitted = (submittedEmail: string) => {
    // Update email state when user submits email for verification
    setEmail(submittedEmail);
    setHasEmail(true);
  };

  const handleEmailVerified = async () => {
    // Close the paywall and reset limit flag
    setShowPaywall(false);
    setIsLimitReached(false);

    // Clean up error messages and move last user question to input field
    setMessages((prev) => {
      let cleanedMessages = [...prev];

      // Find the last user message (the question that triggered the paywall)
      const lastUserMessage = [...cleanedMessages]
        .reverse()
        .find((msg) => msg.isUser);

      // Remove knowledge base error message if it's the last message
      if (cleanedMessages.length > 0) {
        const lastMessage = cleanedMessages[cleanedMessages.length - 1];
        if (lastMessage.text === KNOWLEDGE_BASE_ERROR) {
          cleanedMessages = cleanedMessages.slice(0, -1);
        }
      }

      // Remove limit reached error message if it's now the last message
      if (cleanedMessages.length > 0) {
        const lastMessage = cleanedMessages[cleanedMessages.length - 1];
        if (lastMessage.text.startsWith(LIMIT_REACHED_ERROR)) {
          cleanedMessages = cleanedMessages.slice(0, -1);
        }
      }

      // If we found a user message, remove it from chat and put it in the input field
      if (lastUserMessage) {
        // Remove the user message from chat
        cleanedMessages = cleanedMessages.filter(
          (msg) => msg.id !== lastUserMessage.id,
        );
        // Put the question back in the input field
        setInputMessage(lastUserMessage.text);
      }

      return cleanedMessages;
    });

    // Re-initialize cookie to rehydrate email verification status and remaining messages
    try {
      if (!isInitializing) {
        const response = await callApiWithOptions({
          endpoint: "ComplianceGuide/InitCookie",
          method: HttpMethod.POST,
          customHeaders: isPublic ? getCustomHeaders() : undefined,
        });

        if (response.success) {
          // Update state with fresh data from backend
          setAnonymousId(response.anonymousId);
          setThreadId(response.anonymousId);
          if (response.anonymousId && !localStorage.getItem("anon_Id")) {
            localStorage.setItem("anon_Id", response.anonymousId);
          }
          setEmail(response.email || "");
          setHasEmail(response.hasEmail || false);
          setIsEmailVerified(response.isEmailVerified || false);
        }
      }
    } catch (error: any) {
      console.error(
        "Error re-initializing cookie after email verification:",
        error,
      );
      // Fallback: still update state
      setIsEmailVerified(true);
      setHasEmail(true);
    }
  };

  return (
    <div className={styles.container}>
      <div
        className={styles.chatContainer}
        style={{
          filter: isPublic && showCloudflareTurnstile ? "blur(5px)" : "none",
          pointerEvents: isPublic && showCloudflareTurnstile ? "none" : "auto",
          userSelect: isPublic && showCloudflareTurnstile ? "none" : "auto",
        }}
      >
        <div className={styles.mainContent}>
          <div className={styles.chatHeader}>
            <div className={styles.headerInfo}>
              <i className="bi bi-robot"></i>
              <div>
                <h5 className="mb-0">
                  AI Compliance Assistant
                  {selectedPersona === "donna" && (
                    <small className="text-muted ms-2">(Donna Mode)</small>
                  )}
                </h5>
                <small className="text-muted">
                  Powered by Compliance Guide data
                </small>
              </div>
              <div className={`${styles.headerControls} ms-1`}>
                <TooltipButton
                  className="btn btn-outline-secondary btn-sm"
                  onClick={startVoiceMode}
                  tooltip="Voice Mode - Click to enable voice interaction"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M2 10v3" />
                    <path d="M6 6v11" />
                    <path d="M10 3v18" />
                    <path d="M14 8v7" />
                    <path d="M18 5v13" />
                    <path d="M22 10v3" />
                  </svg>
                </TooltipButton>
              </div>
            </div>
            {/* Header controls - Only show for non-public users */}
            {!isPublic && (
              <div className={`${styles.headerControls}`}>
                <div>
                  <TooltipButton
                    className="btn btn-outline-secondary btn-sm ms-2"
                    onClick={handleClearChat}
                    tooltip="Reset conversation - Clear all messages and start fresh"
                  >
                    <i className="bi bi-arrow-clockwise"></i>
                  </TooltipButton>
                  <TooltipButton
                    className="btn btn-outline-secondary btn-sm ms-2"
                    onClick={() => setShowSettingsModal(true)}
                    tooltip="Settings - Configure AI assistant preferences"
                  >
                    <i className="bi bi-gear"></i>
                  </TooltipButton>
                </div>
              </div>
            )}
          </div>

          <div className={styles.chatBody}>
            {/* Collapsible Sidebar - Only show for non-public users */}
            {!isPublic && (
              <div
                className={`${styles.internalSidebar} ${
                  sidebarCollapsed ? styles.collapsed : styles.expanded
                }`}
              >
                {sidebarCollapsed ? (
                  // Collapsed state - thin bar with icons
                  <div className={styles.collapsedContent}>
                    <TooltipButton
                      className={`btn btn-outline-secondary btn-sm ${styles.expandButton}`}
                      onClick={() => setSidebarCollapsed(false)}
                      tooltip="Expand sidebar"
                      placement="right"
                    >
                      <i className="bi bi-layout-sidebar-inset"></i>
                    </TooltipButton>
                    <TooltipButton
                      className={`btn btn-outline-primary btn-sm ${styles.expandButton}`}
                      onClick={handleNewChat}
                      tooltip="Start a new conversation thread"
                      placement="right"
                    >
                      <i className="bi bi-plus-circle"></i>
                    </TooltipButton>
                  </div>
                ) : (
                  // Expanded state - full sidebar
                  <>
                    <div className={styles.sidebarHeader}>
                      <div>
                        {!sidebarCollapsed && (
                          <a
                            href="#"
                            className={styles.newThreadLink}
                            onClick={(e) => {
                              e.preventDefault();
                              handleNewChat();
                            }}
                            title="Start a new conversation thread"
                          >
                            <i className="bi bi-file-earmark-plus me-2"></i>
                            New Thread
                          </a>
                        )}
                      </div>
                      <TooltipButton
                        className={`btn btn-sm btn-outline-secondary ${styles.collapseButton}`}
                        onClick={() => setSidebarCollapsed(true)}
                        tooltip="Collapse sidebar"
                        placement="right"
                      >
                        <i className="bi bi-chevron-left"></i>
                      </TooltipButton>
                    </div>
                    <div className={styles.sidebarContent}>
                      {sidebarCollapsed && (
                        <a
                          href="#"
                          className={styles.newThreadLink}
                          onClick={(e) => {
                            e.preventDefault();
                            handleNewChat();
                          }}
                          title="Start a new conversation thread"
                        >
                          <i className="bi bi-file-earmark-plus me-2"></i>
                          New Thread
                        </a>
                      )}

                      {/* Threads List */}
                      <div className={styles.threadsSection}>
                        <h6 className={styles.threadsTitle}>Recent Chats</h6>
                        {isLoadingThreads ? (
                          <div className={styles.loadingThreads}>
                            <i className="bi bi-arrow-clockwise spin"></i>
                            Loading chats...
                          </div>
                        ) : threads.length > 0 ? (
                          <div className={styles.threadsList}>
                            {threads.map((thread) => (
                              <div
                                key={thread.threadId}
                                className={styles.threadItem}
                              >
                                <a
                                  href="#"
                                  className={styles.threadLink}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    loadThread(thread.threadId);
                                  }}
                                  title={thread.threadName}
                                >
                                  <i className="bi bi-chat-text me-2"></i>
                                  {thread.threadName}
                                </a>
                                <button
                                  className={styles.deleteThreadBtn}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleDeleteThread(
                                      thread.threadId,
                                      thread.threadName,
                                    );
                                  }}
                                  title="Delete thread"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className={styles.noThreads}>
                            <i className="bi bi-chat-text me-2"></i>
                            No chats yet
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Main Chat Content Area */}
            <div className={styles.chatContent}>
              <div
                className={styles.messagesContainer}
                ref={messagesContainerRef}
                style={{
                  paddingBottom:
                    containerHeight > 0 &&
                    containerHeight - lastMessagesHeight - 40 > 0
                      ? `${containerHeight - lastMessagesHeight - 40}px`
                      : "0px",
                }}
              >
                {messages.map((message) => (
                  <div
                    key={message.id}
                    data-message-id={message.id}
                    className={`${styles.message} ${
                      message.isUser ? styles.userMessage : styles.aiMessage
                    }`}
                  >
                    <div className={styles.messageContent}>
                      <div className={styles.messageText}>
                        {message.isUser ? (
                          message.text === " " ? (
                            // The " " for the typing indcator
                            // is so in voice mode as soon as the
                            // user starts speaking we show
                            // the three dots - Jeff
                            <TypingIndicator
                              className={typingIndicatorStyles.white}
                            />
                          ) : (
                            message.text
                          )
                        ) : // AI messages - show as plain text while streaming, markdown when complete
                        message.isStreamingComplete !== false ? (
                          <AIMarkdown>{message.text}</AIMarkdown>
                        ) : (
                          <AIMarkdown>{message.text}</AIMarkdown>
                          // I believe we can just stream direclty into Markdown
                          // now - Jeff 2025-08-25
                          // <pre
                          //   style={{
                          //     whiteSpace: "pre-wrap",
                          //     margin: 0,
                          //     fontFamily: "inherit",
                          //   }}
                          // >
                          //   {message.text}
                          // </pre>
                        )}
                      </div>
                      <div className={styles.messageTime}>
                        {message.timestamp.toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>

                      {/* Feedback buttons - show for AI messages but not welcome message or error responses */}
                      {!message.isUser &&
                        message.id !== "welcome" &&
                        message.aiInteractionId && (
                          <div className={styles.feedbackContainer}>
                            <div className={styles.feedbackButtons}>
                              <button
                                className={`btn btn-sm ${styles.feedbackBtn} ${
                                  message.feedback?.isLiked === true
                                    ? styles.active
                                    : ""
                                }`}
                                onClick={() => handleFeedback(message.id, true)}
                                title="Like this response"
                              >
                                <i className="bi bi-hand-thumbs-up"></i>
                              </button>
                              <button
                                className={`btn btn-sm ${styles.feedbackBtn} ${
                                  message.feedback?.isLiked === false
                                    ? styles.active
                                    : ""
                                }`}
                                onClick={() =>
                                  handleFeedback(message.id, false)
                                }
                                title="Dislike this response"
                              >
                                <i className="bi bi-hand-thumbs-down"></i>
                              </button>
                              <button
                                className={`btn btn-sm ${styles.feedbackBtn} ${
                                  message.feedback?.isReported
                                    ? styles.active
                                    : ""
                                }`}
                                onClick={() => handleReport(message.id)}
                                title="Report this response"
                              >
                                <i className="bi bi-flag"></i>
                              </button>
                            </div>

                            {/* Report input */}
                            {showReportInput === message.id && (
                              <div className={styles.reportInput}>
                                <textarea
                                  className="form-control form-control-sm"
                                  placeholder="Optional: Tell us what's wrong with this response..."
                                  value={reportMessage}
                                  onChange={(e) =>
                                    setReportMessage(e.target.value)
                                  }
                                  rows={2}
                                />
                                <div className={styles.reportActions}>
                                  <button
                                    className="btn btn-primary btn-sm"
                                    onClick={() => handleReport(message.id)}
                                  >
                                    Submit Report
                                  </button>
                                  <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => {
                                      setShowReportInput(null);
                                      setReportMessage("");
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      {/* Debug RAG content panel */}
                      {debugMode && message.ragContent && !message.isUser && (
                        <details className={styles.debugPanel}>
                          <summary className={styles.debugPanelHeader}>
                            <i className="bi bi-bug me-2"></i>
                            RAG Context ({message.ragContent.length} chars)
                          </summary>
                          <pre className={styles.debugPanelContent}>
                            {message.ragContent}
                          </pre>
                        </details>
                      )}
                    </div>
                  </div>
                ))}

                {isTyping && (
                  <div className={`${styles.message} ${styles.aiMessage}`}>
                    <div className={styles.messageContent}>
                      <TypingIndicator />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {showVoiceMode ? (
                <VoiceMode
                  onClose={handleCloseVoiceMode}
                  selectedPersona={selectedPersona}
                  selectedVoice={selectedVoice}
                  threadId={threadId}
                  isPublic={isPublic}
                  onUserTranscriptionDelta={handleUserTranscriptionDelta}
                  onUserTranscriptionComplete={handleUserTranscriptionComplete}
                  onAIResponseDelta={handleAIResponseDelta}
                  onAIResponseComplete={handleAIResponseComplete}
                />
              ) : (
                <div className={styles.inputContainer}>
                  <div className={styles.inputGroup}>
                    <textarea
                      ref={inputRef}
                      className={`form-control ${styles.messageInput}`}
                      placeholder={
                        cloudflareTurnstileRequired
                          ? "Security check required..."
                          : "Ask compliance questions..."
                      }
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      rows={1}
                      disabled={isLoading || cloudflareTurnstileRequired}
                    />
                    <button
                      className="btn btn-primary"
                      onClick={handleSendMessage}
                      disabled={
                        isLoading ||
                        !inputMessage.trim() ||
                        cloudflareTurnstileRequired
                      }
                    >
                      {isLoading ? (
                        <i className="bi bi-hourglass-split"></i>
                      ) : (
                        <i className="bi bi-send"></i>
                      )}
                    </button>
                  </div>

                  <div className={styles.suggestions}>
                    <small className="text-muted">Try asking:</small>
                    <div className={styles.suggestionButtons}>
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() =>
                          setInputMessage(
                            `What are the no call holidays in Louisiana in ${new Date().getFullYear()}?`,
                          )
                        }
                        disabled={isLoading || cloudflareTurnstileRequired}
                      >
                        No call holidays in Louisiana
                      </button>
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() =>
                          setInputMessage(
                            "What are the calling time restrictions for Texas?",
                          )
                        }
                        disabled={isLoading || cloudflareTurnstileRequired}
                      >
                        Texas calling times
                      </button>
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() =>
                          setInputMessage(
                            "Do I need written consent for robocalls?",
                          )
                        }
                        disabled={isLoading || cloudflareTurnstileRequired}
                      >
                        Robocall consent requirements
                      </button>
                    </div>
                  </div>

                  <div className={`${styles.bottomCard}`}>
                    <p className="small muted mb-0 p-1">
                      <i className="bi bi-exclamation-triangle"></i>{" "}
                      <span className={styles.importantText}>Important</span>{" "}
                      The AI Assistant provides general guidance based on our
                      compliance database. For specific legal advice, please
                      consult with a qualified attorney.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Settings Modal */}
          {showSettingsModal && (
            <>
              <div className="modal-backdrop fade show"></div>
              <div className="modal fade show d-block" tabIndex={-1}>
                <div className="modal-dialog modal-lg">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">
                        <i className="bi bi-gear me-2"></i>
                        Settings
                      </h5>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setShowSettingsModal(false)}
                        aria-label="Close settings modal"
                      ></button>
                    </div>
                    <div className="modal-body">
                      {/* Voice Selection */}
                      <div className="mb-4">
                        <h6>
                          <i className="bi bi-mic me-2"></i>
                          Voice Selection
                        </h6>
                        <p className="small mb-2 text-muted">
                          Choose the voice for the AI assistant:
                        </p>
                        <div className="alert alert-info small mb-3">
                          <i className="bi bi-info-circle me-1"></i>
                          <strong>Note:</strong> Changing the voice will reset
                          the conversation.
                        </div>
                        <div className="row">
                          <div className="col-md-6">
                            <div className="form-check mb-2">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="voice"
                                id="voiceAlloy"
                                checked={selectedVoice === "alloy"}
                                onChange={() => handleVoiceChange("alloy")}
                              />
                              <label
                                className="form-check-label"
                                htmlFor="voiceAlloy"
                              >
                                Alloy
                              </label>
                            </div>
                            <div className="form-check mb-2">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="voice"
                                id="voiceAsh"
                                checked={selectedVoice === "ash"}
                                onChange={() => handleVoiceChange("ash")}
                              />
                              <label
                                className="form-check-label"
                                htmlFor="voiceAsh"
                              >
                                Ash
                              </label>
                            </div>
                            <div className="form-check mb-2">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="voice"
                                id="voiceBallad"
                                checked={selectedVoice === "ballad"}
                                onChange={() => handleVoiceChange("ballad")}
                              />
                              <label
                                className="form-check-label"
                                htmlFor="voiceBallad"
                              >
                                Ballad
                              </label>
                            </div>
                            <div className="form-check mb-2">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="voice"
                                id="voiceCoral"
                                checked={selectedVoice === "coral"}
                                onChange={() => handleVoiceChange("coral")}
                              />
                              <label
                                className="form-check-label"
                                htmlFor="voiceCoral"
                              >
                                Coral
                              </label>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-check mb-2">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="voice"
                                id="voiceEcho"
                                checked={selectedVoice === "echo"}
                                onChange={() => handleVoiceChange("echo")}
                              />
                              <label
                                className="form-check-label"
                                htmlFor="voiceEcho"
                              >
                                Echo
                              </label>
                            </div>
                            <div className="form-check mb-2">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="voice"
                                id="voiceSage"
                                checked={selectedVoice === "sage"}
                                onChange={() => handleVoiceChange("sage")}
                              />
                              <label
                                className="form-check-label"
                                htmlFor="voiceSage"
                              >
                                Sage
                              </label>
                            </div>
                            <div className="form-check mb-2">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="voice"
                                id="voiceShimmer"
                                checked={selectedVoice === "shimmer"}
                                onChange={() => handleVoiceChange("shimmer")}
                              />
                              <label
                                className="form-check-label"
                                htmlFor="voiceShimmer"
                              >
                                Shimmer
                              </label>
                            </div>
                            <div className="form-check mb-2">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="voice"
                                id="voiceVerse"
                                checked={selectedVoice === "verse"}
                                onChange={() => handleVoiceChange("verse")}
                              />
                              <label
                                className="form-check-label"
                                htmlFor="voiceVerse"
                              >
                                Verse
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Help Information */}
                      <div className="mb-4">
                        <h6>
                          <i className="bi bi-info-circle me-2"></i>
                          How it works
                        </h6>
                        <p>
                          Our AI Assistant uses the comprehensive Compliance
                          Guide database to provide accurate answers about
                          telecommunications compliance regulations.
                        </p>
                        <ul>
                          <li>Ask questions in natural language</li>
                          <li>Get instant answers from our legal database</li>
                          <li>Receive citations and references</li>
                          <li>Access state-specific information</li>
                        </ul>
                      </div>

                      {/* Example Questions */}
                      <div className="mb-4">
                        <h6>
                          <i className="bi bi-lightbulb me-2"></i>
                          Example Questions
                        </h6>
                        <ul>
                          <li>
                            "What are the DNC requirements for California?"
                          </li>
                          <li>"When can I call consumers in New York?"</li>
                          <li>"What holidays restrict calling in Florida?"</li>
                          <li>"Do I need consent for political calls?"</li>
                          <li>"What are the TCPA safe harbor provisions?"</li>
                        </ul>
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setShowSettingsModal(false)}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal fade show d-block" tabIndex={-1}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="bi bi-exclamation-triangle text-danger me-2"></i>
                    Confirm Delete
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={cancelDeleteThread}
                    aria-label="Close delete confirmation modal"
                  ></button>
                </div>
                <div className="modal-body">
                  <p>
                    Are you sure you want to delete{" "}
                    <strong>"{threadToDelete?.name}"</strong>?
                  </p>
                  <p className="text-muted small mb-0">
                    This action cannot be undone. All messages in this thread
                    will be permanently deleted.
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={cancelDeleteThread}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={confirmDeleteThread}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Paywall Modal - Only show for public users */}
      {isPublic && (
        <PaywallModal
          isOpen={showPaywall}
          onClose={handlePaywallClose}
          onEmailVerified={handleEmailVerified}
          onEmailSubmitted={handleEmailSubmitted}
          isEmailVerified={isEmailVerified}
          hasEmail={hasEmail}
        />
      )}

      {/* CloudflareTurnstile Security Modal - Only show for public users */}
      {isPublic && showCloudflareTurnstile && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{
              zIndex: 9999,
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              backdropFilter: "blur(3px)",
            }}
          ></div>
          <div
            className="modal fade show d-block"
            tabIndex={-1}
            style={{ zIndex: 10000 }}
            onKeyDown={(e) => {
              // Prevent ESC key from closing modal
              if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            onClick={(e) => {
              // Prevent clicking outside modal to close it
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <div
              className="modal-dialog modal-dialog-centered"
              onClick={(e) => {
                // Prevent event bubbling from modal content
                e.stopPropagation();
              }}
            >
              <div className="modal-content">
                <div className="modal-header border-0 pb-0">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-shield-exclamation text-warning me-3 fs-2"></i>
                    <div>
                      <h4 className="modal-title mb-0">
                        Security Verification Required
                      </h4>
                      <small className="text-muted">
                        Complete the security check to continue
                      </small>
                    </div>
                  </div>
                </div>
                <div className="modal-body text-center py-4">
                  <p className="mb-4">
                    Due to high request activity from your IP address, we need
                    to verify you're not a bot. Please complete the security
                    challenge below to continue using our AI Assistant.
                  </p>

                  {/* Cloudflare Turnstile Widget */}
                  <div className="d-flex justify-content-center mb-4">
                    <Turnstile
                      siteKey={
                        import.meta.env.VITE_CLOUDFLARE_SITEKEY ||
                        "0x4AAAAAAABkMYinukE8nzYV"
                      }
                      onSuccess={handleCloudflareTurnstileSuccess}
                      onError={(error) => {
                        console.error("Turnstile error:", error);
                      }}
                      onExpire={() => {
                        console.log("Turnstile expired");
                      }}
                      onTimeout={() => {
                        console.log("Turnstile timeout");
                      }}
                      options={{
                        theme: "light",
                        size: "normal",
                      }}
                    />
                  </div>

                  <div className="alert alert-info small">
                    <i className="bi bi-info-circle me-2"></i>
                    This security check helps protect our service from abuse and
                    ensures fair usage for all users.
                  </div>
                </div>
                <div className="modal-footer border-0 pt-0">
                  <div className="w-100 text-center">
                    <small className="text-muted">
                      <i className="bi bi-lock me-1"></i>
                      This verification is required and cannot be skipped
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AIAssistant;
